import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Client } from '@ddv/models';
import { DropdownOption } from '@hs/ui-core-common';
import { CommonDate, DateRange, DateRangeSelectDropdownComponent, DateSelectDropdownComponent, FuzzyDate, PeriodStatusCollection } from '@hs/ui-core-date';
import { MultiSelectDropdownComponent, SingleSelectDropdownComponent } from '@hs/ui-core-dropdowns';
import { Fund, FundMultiSelectDropdownComponent } from '@hs/ui-core-fund';

@Component({
    selector: 'ddv-query-params',
    templateUrl: './query-params.component.html',
    styleUrls: ['./query-params.component.scss'],
    imports: [
        FundMultiSelectDropdownComponent,
        MultiSelectDropdownComponent,
        SingleSelectDropdownComponent,
        DateRangeSelectDropdownComponent,
        DateSelectDropdownComponent,
    ],
})
export class QueryParamsComponent implements OnChanges {
    @Input() clientMode: 'funds' | 'single-client' | 'multi-client' = 'funds';
    @Input() funds: Fund[] = [];
    @Input() selectedFunds: Fund[] = [];
    @Input() clients: Client[] = [];
    @Input() selectedClients: Client[] = [];
    @Input() dateMode: 'none' | 'point-in-time' | 'date-range' = 'date-range';
    @Input() fuzzyDates: FuzzyDate[] = [];
    @Input() fromDate: string | undefined;
    @Input() toDate: string | undefined;
    @Input() periodStatuses: PeriodStatusCollection | undefined;

    @Output() selectedFundsChange = new EventEmitter<Fund[]>();
    @Output() selectedClientsChange = new EventEmitter<Client[]>();
    @Output() dateRangeChange = new EventEmitter<{ from?: string, to?: string }>();
    @Output() dateChange = new EventEmitter<string>();

    protected clientOptions: DropdownOption[] = [];
    protected selectedClientOption: DropdownOption | undefined;
    protected selectedClientsOptions: DropdownOption[] = [];
    protected dateRange: DateRange | undefined;
    protected dateRangeLabels: string[] | undefined = ['from date', 'to date'];
    protected date: FuzzyDate | CommonDate | undefined;
    protected dateLabel: string | undefined = 'date';

    ngOnChanges(): void {
        this.configureClientSelectors();
        this.configureDateSelectors();
    }

    protected onSelectedClientsChanged(): void {
        this.selectedClients = this.clients.filter((client) => {
            return this.selectedClientsOptions.some((option) => option.value === client.clientId);
        });
        this.selectedClientsChange.emit(this.selectedClients);
    }

    protected onSelectedClientChanged(): void {
        this.selectedClients = this.clients.filter((client) => client.clientId === this.selectedClientOption?.value);
        this.selectedClientsChange.emit(this.selectedClients);
    }

    protected onDateRangeChanged(): void {
        // if you clear the range from the anchor, the dateRange will be undefined
        // but, if you open the picker, delete only the from-date, and close it, we get here with an incomplete range
        if (this.dateRange && !this.dateRange.isComplete()) {
            return;
        }

        const tuple = this.dateRange?.toTuple('us');
        this.dateRangeChange.emit({
            from: tuple?.[0],
            to: tuple?.[1],
        });
    }

    protected onDateChanged(): void {
        if (!this.date) {
            return;
        }

        this.dateChange.emit(this.date instanceof CommonDate ? this.date.toNamedFormat('us') : this.date.name);
    }

    private configureClientSelectors(): void {
        this.clientOptions = this.clients.map((client) => {
            return {
                text: client.clientName,
                value: client.clientId,
            };
        });

        if (this.clientMode === 'single-client') {
            this.selectedClientsOptions = [];
            this.selectedClientOption = this.clientOptions.find((option) => option.value === this.selectedClients[0]?.clientId);
        } else {
            this.selectedClientsOptions = this.clientOptions.filter((option) => {
                return this.selectedClients.some((client) => client.clientId === option.value);
            });
            this.selectedClientOption = undefined;
        }
    }

    private configureDateSelectors(): void {
        if (this.dateMode === 'date-range') {
            this.configureDateRangeMode();
        } else {
            this.configureSingleDateMode();
        }
    }

    private configureDateRangeMode(): void {
        this.dateRange = new DateRange(this.parseDateString(this.fromDate), this.parseDateString(this.toDate));
        this.dateRangeLabels = [
            this.dateRange?.from instanceof FuzzyDate ? 'dynamic from' : 'from date',
            this.dateRange?.to instanceof FuzzyDate ? 'dynamic to' : 'to date',
        ];
        this.date = undefined;
        this.dateLabel = undefined;
    }

    private configureSingleDateMode(): void {
        this.date = this.parseDateString(this.fromDate);
        this.dateLabel = this.date instanceof FuzzyDate ? 'dynamic date' : 'date';
        this.dateRange = undefined;
        this.dateRangeLabels = undefined;
    }

    private parseDateString(dateString: string | undefined): FuzzyDate | CommonDate | undefined {
        if (!dateString) {
            return undefined;
        }

        const fuzzyDate = this.fuzzyDates.find((fd) => fd.name === dateString);
        if (fuzzyDate) {
            return fuzzyDate;
        }

        try {
            return CommonDate.fromUSFormat(dateString);
        } catch (e) {
            console.error(`Date string [${dateString}] is neither a fuzzy nor a valid date string in US format`);
            return undefined;
        }
    }
}
